import { z } from 'zod';

// Body for both create and update. Mirrors legacy CameraDTO with conditional
// requirements: when `vod` is set, the live-stream fields (ipAddress / port /
// username / password) are not required; otherwise they are. `channel` is only
// required when the camera is attached to an NVR. On update `password` is never
// required — responses strip it, so an omitted value means "keep the stored one".
const cameraBodyBase = z.object({
  patientId: z.uuid(),
  name: z.string().min(1),
  ipAddress: z.ipv4().optional(),
  port: z.string().regex(/^\d+$/, { message: 'port must be numeric' }).optional(),
  username: z.string().min(1).optional(),
  password: z.string().min(1).optional(),
  vod: z.string().min(1).optional(),
  channel: z.string().min(1).optional(),
  nvrId: z.uuid().optional(),
  areaDescription: z.string().max(500).optional(),
  irDualCapture: z.boolean().optional(),
});

type LiveStreamField = 'ipAddress' | 'port' | 'username' | 'password';

function refineCameraBody<T extends typeof cameraBodyBase>(schema: T, requiredWithoutVod: readonly LiveStreamField[]) {
  return schema.superRefine((val, ctx) => {
    if (!val.vod) {
      for (const f of requiredWithoutVod) {
        if (!val[f]) {
          ctx.addIssue({
            code: 'custom',
            path: [f],
            message: `${f} is required when vod is not provided`,
          });
        }
      }
    }
    if (val.nvrId && !val.channel) {
      ctx.addIssue({
        code: 'custom',
        path: ['channel'],
        message: 'channel is required when nvrId is provided',
      });
    }
  });
}

export const createCameraBody = refineCameraBody(cameraBodyBase, ['ipAddress', 'port', 'username', 'password']);
export type CreateCameraBody = z.infer<typeof createCameraBody>;

export const updateCameraBody = refineCameraBody(cameraBodyBase, ['ipAddress', 'port', 'username']);
export type UpdateCameraBody = z.infer<typeof updateCameraBody>;

export const cameraIdParam = z.object({ id: z.string().min(1) });
export type CameraIdParam = z.infer<typeof cameraIdParam>;

export const toggleIrLightBody = z.object({ enabled: z.boolean() });
export type ToggleIrLightBody = z.infer<typeof toggleIrLightBody>;

export const toggleIrLightResponse = z.object({
  id: z.string(),
  irCutFilter: z.boolean().nullable(),
  irIlluminator: z.boolean().nullable(),
});
export type ToggleIrLightResponse = z.infer<typeof toggleIrLightResponse>;

export const cameraZonePoint = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
});
export type CameraZonePoint = z.infer<typeof cameraZonePoint>;

export const cameraZoneType = z.enum([
  'bed',
  'chair',
  'couch',
  'wheelchair',
  'toilet',
  'bathroom',
  'kitchen',
  'doorway',
  'floor',
  'other',
]);
export type CameraZoneType = z.infer<typeof cameraZoneType>;

export const cameraZone = z.object({
  id: z.string().min(1),
  label: z.string().min(1).max(40),
  type: cameraZoneType.nullable().optional(),
  corners: z.array(cameraZonePoint).length(4),
});
export type CameraZone = z.infer<typeof cameraZone>;

export const updateCameraZonesBody = z.object({
  zones: z.array(cameraZone).max(40),
});
export type UpdateCameraZonesBody = z.infer<typeof updateCameraZonesBody>;

export const cameraVisionPreprocessing = z.enum(['auto', 'none', 'ir_normalize', 'contrast', 'denoise']);
export type CameraVisionPreprocessing = z.infer<typeof cameraVisionPreprocessing>;

export const cameraVisionStreamQuality = z.enum(['low', 'high']);
export type CameraVisionStreamQuality = z.infer<typeof cameraVisionStreamQuality>;

export const visionModelSettings = z.object({
  preprocessing: cameraVisionPreprocessing,
  imageSize: z.union([z.literal(640), z.literal(960), z.literal(1280)]),
  modelSize: z.enum(['n', 's', 'm']),
  confidence: z.number().min(0.05).max(0.95),
});
export type VisionModelSettings = z.infer<typeof visionModelSettings>;

export const cameraVisionSettings = visionModelSettings.extend({
  streamQuality: cameraVisionStreamQuality,
});
export type CameraVisionSettings = z.infer<typeof cameraVisionSettings>;

export const DEFAULT_CAMERA_VISION_SETTINGS: CameraVisionSettings = {
  streamQuality: 'low',
  preprocessing: 'auto',
  imageSize: 640,
  modelSize: 's',
  confidence: 0.35,
};

export function parseCameraVisionSettings(raw: unknown): CameraVisionSettings {
  const parsed = cameraVisionSettings.safeParse(raw);
  return parsed.success ? parsed.data : DEFAULT_CAMERA_VISION_SETTINGS;
}

export function visionModelSettingsOf(settings: CameraVisionSettings): VisionModelSettings {
  const { preprocessing, imageSize, modelSize, confidence } = settings;
  return { preprocessing, imageSize, modelSize, confidence };
}

export function parseCameraZones(raw: unknown): CameraZone[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap((item: unknown) => {
    const parsed = cameraZone.safeParse(item);
    return parsed.success ? [parsed.data] : [];
  });
}

export const updateCameraVisionSettingsBody = cameraVisionSettings;
export type UpdateCameraVisionSettingsBody = z.infer<typeof updateCameraVisionSettingsBody>;

export const getCamerasQuery = z.object({
  patient: z.string().optional(),
});
export type GetCamerasQuery = z.infer<typeof getCamerasQuery>;

// Safe response shape: explicitly excludes `password`. Used to map Camera rows
// (and rows with included relations) before returning to clients.
export const cameraDetailsResponse = z.object({
  id: z.string(),
  name: z.string(),
  vod: z.string().nullable().optional(),
  ipAddress: z.string(),
  port: z.string(),
  username: z.string(),
  patientId: z.string().nullable().optional(),
  routerId: z.string().nullable().optional(),
  nvrId: z.string().nullable().optional(),
  channel: z.string().nullable().optional(),
  deleted: z.date().nullable().optional(),
  lastActivity: z.date().nullable().optional(),
  motionDetection: z.boolean(),
  lastReachable: z.date().nullable().optional(),
  irCutFilter: z.boolean().nullable().optional(),
  irIlluminator: z.boolean().nullable().optional(),
  irSyncedAt: z.date().nullable().optional(),
  areaDescription: z.string().nullable().optional(),
  irDualCapture: z.boolean().optional(),
  metadata: z.unknown().nullable().optional(),
  patient: z.unknown().nullable().optional(),
  nvr: z.unknown().nullable().optional(),
  zones: z.array(cameraZone).nullable().optional(),
  visionSettings: cameraVisionSettings.nullable().optional(),
});
export type CameraDetailsResponse = z.infer<typeof cameraDetailsResponse>;
